import { Injectable, NgZone } from '@angular/core';

const SELECT_PANEL_MAX_HEIGHT = 256;
const SELECT_PANEL_PADDING_X = 16;
const SELECT_PANEL_INDENT_PADDING_X = 32;
const SELECT_MULTIPLE_PANEL_PADDING_X = 40;
const SELECT_PANEL_VIEWPORT_PADDING = 8;

@Injectable({ providedIn: 'root' })
export class LegacySelectPositionService {
  private readonly handledPanels = new WeakSet<HTMLElement>();

  constructor(private zone: NgZone) {}

  init(): void {
    this.zone.runOutsideAngular(() => {
      const observer = new MutationObserver(records => {
        for (const record of records) {
          for (const node of record.addedNodes) {
            if (node instanceof HTMLElement && (
              node.classList.contains('cdk-overlay-popover') ||
              node.classList.contains('cdk-overlay-pane') ||
              node.classList.contains('cdk-overlay-connected-position-bounding-box') ||
              node.classList.contains('mat-mdc-select-panel')
            )) {
              this.findPanels(node);
            }
          }
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });
    });
  }

  private findPanels(node: HTMLElement): void {
    if (node.matches('.mat-mdc-select-panel')) {
      this.schedulePanel(node);
    }
    node.querySelectorAll<HTMLElement>('.mat-mdc-select-panel').forEach(panel => this.schedulePanel(panel));
  }

  private schedulePanel(panel: HTMLElement): void {
    if (this.handledPanels.has(panel)) {
      return;
    }
    this.handledPanels.add(panel);
    requestAnimationFrame(() => this.positionPanel(panel));
  }

  private positionPanel(panel: HTMLElement): void {
    if (!panel.isConnected || !panel.id) {
      return;
    }

    const host = document.querySelector<HTMLElement>('[aria-controls="' + CSS.escape(panel.id) + '"]');
    const trigger = host?.querySelector<HTMLElement>('.mat-mdc-select-trigger');
    const pane = panel.closest<HTMLElement>('.cdk-overlay-pane');
    if (!trigger || !pane) {
      return;
    }

    pane.style.translate = '';
    const triggerRect = trigger.getBoundingClientRect();
    const triggerFontSize = parseInt(getComputedStyle(trigger).fontSize || '0', 10);
    const multiple = panel.getAttribute('aria-multiselectable') === 'true';
    pane.style.minWidth = `${triggerRect.width}px`;
    pane.style.fontSize = `${triggerFontSize}px`;
    panel.style.minWidth = `calc(100% + ${multiple ? 64 : 32}px)`;
    const paneWidth = pane.offsetWidth;
    const paneHeight = pane.offsetHeight;
    const itemHeight = triggerFontSize * 3;
    if (!itemHeight || !paneWidth || !paneHeight) {
      return;
    }

    const options = Array.from(panel.querySelectorAll<HTMLElement>('mat-option, .mat-mdc-option'));
    const labels = Array.from(panel.querySelectorAll<HTMLElement>('.mat-mdc-optgroup-label'));
    const selected = options.find(option => option.getAttribute('aria-selected') === 'true');
    const firstOption = selected || options[0];
    if (!firstOption) {
      return;
    }

    const selectedOptionIndex = selected ? options.indexOf(selected) : 0;
    const labelsBeforeOption = labels.filter(label =>
      Boolean(label.compareDocumentPosition(firstOption) & Node.DOCUMENT_POSITION_FOLLOWING)
    ).length;
    const selectedIndex = selectedOptionIndex + labelsBeforeOption;
    const itemCount = options.length + labels.length;
    const legacyPanelHeight = Math.min(itemCount * itemHeight, SELECT_PANEL_MAX_HEIGHT);
    const maxScroll = itemCount * itemHeight - legacyPanelHeight;
    const scrollBuffer = legacyPanelHeight / 2;
    const optimalScrollPosition = itemHeight * selectedIndex - scrollBuffer + itemHeight / 2;
    let scrollTop = Math.min(Math.max(0, optimalScrollPosition), maxScroll);

    const optionHeightAdjustment = (itemHeight - triggerRect.height) / 2;
    const maxOptionsDisplayed = Math.floor(SELECT_PANEL_MAX_HEIGHT / itemHeight);
    let optionOffsetFromPanelTop: number;
    if (scrollTop === 0) {
      optionOffsetFromPanelTop = selectedIndex * itemHeight;
    } else if (scrollTop === maxScroll) {
      const firstDisplayedIndex = itemCount - maxOptionsDisplayed;
      const selectedDisplayIndex = selectedIndex - firstDisplayedIndex;
      const partialItemHeight = itemHeight - ((itemCount * itemHeight - SELECT_PANEL_MAX_HEIGHT) % itemHeight);
      optionOffsetFromPanelTop = selectedDisplayIndex * itemHeight + partialItemHeight;
    } else {
      optionOffsetFromPanelTop = scrollBuffer - itemHeight / 2;
    }
    let offsetY = Math.round(optionOffsetFromPanelTop * -1 - optionHeightAdjustment);

    const topSpaceAvailable = triggerRect.top - SELECT_PANEL_VIEWPORT_PADDING;
    const bottomSpaceAvailable = window.innerHeight - triggerRect.bottom - SELECT_PANEL_VIEWPORT_PADDING;
    const panelHeightTop = Math.abs(offsetY);
    const totalPanelHeight = Math.min(itemCount * itemHeight, SELECT_PANEL_MAX_HEIGHT);
    const panelHeightBottom = totalPanelHeight - panelHeightTop - triggerRect.height;
    if (panelHeightBottom > bottomSpaceAvailable) {
      const distanceBelowViewport = Math.round(panelHeightBottom - bottomSpaceAvailable);
      scrollTop -= distanceBelowViewport;
      offsetY -= distanceBelowViewport;
      if (scrollTop <= 0) {
        scrollTop = 0;
        offsetY = 0;
      }
    } else if (panelHeightTop > topSpaceAvailable) {
      const distanceAboveViewport = Math.round(panelHeightTop - topSpaceAvailable);
      scrollTop += distanceAboveViewport;
      offsetY += distanceAboveViewport;
      if (scrollTop >= maxScroll) {
        scrollTop = maxScroll;
        offsetY = 0;
      }
    }

    const paddingWidth = multiple
      ? SELECT_MULTIPLE_PANEL_PADDING_X + SELECT_PANEL_PADDING_X
      : SELECT_PANEL_PADDING_X * 2;
    // The port assumes LTR because the app has no RTL layout.
    let offsetX = multiple
      ? -SELECT_MULTIPLE_PANEL_PADDING_X
      : firstOption.closest('mat-optgroup') ? -SELECT_PANEL_INDENT_PADDING_X : -SELECT_PANEL_PADDING_X;
    const overlayLeft = triggerRect.left;
    const overlayRight = overlayLeft + paneWidth;
    const leftOverflow = 0 - (overlayLeft + offsetX);
    const rightOverflow = overlayRight + offsetX - window.innerWidth + paddingWidth;
    if (leftOverflow > 0) {
      offsetX += leftOverflow + SELECT_PANEL_VIEWPORT_PADDING;
    } else if (rightOverflow > 0) {
      offsetX -= rightOverflow + SELECT_PANEL_VIEWPORT_PADDING;
    }
    offsetX = Math.round(offsetX);

    const viewportHeight = window.innerHeight;
    const fits = (top: number) => top >= 0 && top + paneHeight <= viewportHeight;
    const visible = (top: number) => Math.max(0, Math.min(top + paneHeight, viewportHeight) - Math.max(top, 0));
    const primaryTop = triggerRect.top + offsetY;
    const fallbackTop = triggerRect.bottom + offsetY - paneHeight;
    const useFallback = !fits(primaryTop) && (fits(fallbackTop) || visible(fallbackTop) > visible(primaryTop));

    this.applyTranslate(pane, triggerRect.left + offsetX, useFallback ? fallbackTop : primaryTop);
    panel.scrollTop = scrollTop;
    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => {
        if (!panel.isConnected) {
          resizeObserver.disconnect();
          return;
        }
        const currentTriggerRect = trigger.getBoundingClientRect();
        const currentPaneHeight = pane.offsetHeight;
        const targetTop = useFallback
          ? currentTriggerRect.bottom + offsetY - currentPaneHeight
          : currentTriggerRect.top + offsetY;
        this.applyTranslate(pane, currentTriggerRect.left + offsetX, targetTop);
      });
      resizeObserver.observe(panel);
    }
  }

  private applyTranslate(pane: HTMLElement, left: number, top: number): void {
    // CDK owns the pane's top/left and transform; translate composes with them.
    pane.style.translate = '';
    const paneRect = pane.getBoundingClientRect();
    pane.style.translate = `${left - paneRect.left}px ${top - paneRect.top}px`;
  }
}
